import React, { useContext, useEffect, useState } from "react";
import IOrderUI from "../../../models/IOrderUI";
import OwTitle from "../OwTitle/OwTitle";
import OwLabel from "../OwLabel/OwLabel";
import SupplierContext from "../../../contexts/SupplierContext";
import AddressDisplay from "../AddressDisplay/AddressDisplay";
import OwButton from "../OwButton/OwButton";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import { Socket } from "socket.io-client";
import IGetOptimizedRouteDTO from "../../../models/DTO/IGetOptimizedRouteDTO";
import UserContext from "../../../contexts/UserContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetOptimizedRouteDTO from "../../../models/DTO/IOnGetOptimizedRouteDTO";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import BetaBadge from "../BetaBadge/BetaBadge";

type Props = {
    socket: Socket;
    orders: { guid: string; name: string; qbd_TxnID?: string; order_batch_id?: number | null; order: IOrderUI }[];
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    closeFn: (refreshData?: boolean) => void;
};

export const RouteOptimizationModal = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [processingRouteActive, setProcessingRouteActive] = useState(false);
    const [optimizedRoute, setOptimizedRoute] = useState<IOnGetOptimizedRouteDTO | null>(null);

    const getRouteOptimization = () => {
        const startAddress = supplierModel.Ship_To;

        const addresses = props.orders
            .filter((order) => !!order.order.Ship_To)
            .map((order) => {
                return { detail: order.order.Ship_To!, orderGuid: order.guid, orderName: order.name, orderId: order.order.id };
            });

        const payload: IGetOptimizedRouteDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            startAddress,
            addresses: addresses.reverse(),
            endAddress: startAddress,
        };

        props.socket.emit(EndpointsEnum.ShipmentGetOptimizedRoute, payload);

        setProcessingRouteActive(true);
    };

    const onGetRouteOptimization = (data: IOnGetOptimizedRouteDTO) => {
        console.log("onGetRouteOptimization", data);
        setOptimizedRoute(data);
        setProcessingRouteActive(false);
    };

    const notifyFn = () => {
        setProcessingRouteActive(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.ShipmentOnGetOptimizedRoute, fn: onGetRouteOptimization },
        { endpoint: EndpointsEnum.Notify, fn: notifyFn },
    ];

    const setupSockets = () => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }
    };

    const tearDownSockets = () => {
        for (const socket of sockets) {
            props.socket.removeListener(socket.endpoint, socket.fn);
        }
    };

    useEffect(() => {
        setupSockets();

        return () => {
            tearDownSockets();
        };
    }, []);

    const sortOptimizedRoute = (a: { optimizedIndex: number }, b: { optimizedIndex: number }) => {
        if (a.optimizedIndex < b.optimizedIndex) {
            return -1;
        } else if (a.optimizedIndex > b.optimizedIndex) {
            return 1;
        } else {
            return 0;
        }
    };

    const downloadCsv = () => {
        const csvHeader =
            "data:text/csv;charset=utf-8,Stop, Name, Order, Address Line 1, Address Line 2, Address Line 3, City, State, Postal Code, Phone\n";

        const csvContent =
            csvHeader +
            optimizedRoute!.optimizedRoute
                .sort(sortOptimizedRoute)
                .map((detail, index) => {
                    return `${index + 1},${detail.detail.name},${detail.orderName},${detail.detail.address_line_one || ""},${
                        detail.detail.address_line_two || ""
                    },${detail.detail.address_line_three || ""},${detail.detail.city || ""},${detail.detail.state || ""},${
                        detail.detail.postal_code || ""
                    },${detail.detail.phone || ""}`;
                })
                .join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "optimized_route.csv");
        document.body.appendChild(link); // Required for FF

        link.click();
    };

    return (
        <div className={"RouteOptimizationModal p-3"}>
            <div className={"d-flex justify-content-between"}>
                <div className={"d-flex"}>
                    <OwTitle title={"route_optimization"} leftIconName={"fad fa-route"} />

                    <div>
                        <BetaBadge />
                    </div>
                </div>

                <div>
                    <OwButton centerIconClass={"fas fa-times"} fn={() => props.closeFn()} className={"btn-danger"} />
                </div>
            </div>

            <OwLabel title={"start"} isBold={true} isUnderline={true} />
            <AddressDisplay address={supplierModel.Ship_To} />
            {optimizedRoute ? (
                <div className={"mt-4"}>
                    <OwLabel title={"optimized_route"} isBold={true} isUnderline={true} />
                    {optimizedRoute.optimizedRoute.sort(sortOptimizedRoute).map((detail, index) => {
                        return (
                            <div key={detail.orderGuid}>
                                {detail.detail.name} {detail.orderName}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className={"mt-4"}>
                    <OwLabel title={"orders"} isBold={true} isUnderline={true} />
                    <div>
                        {props.orders.map((order) => {
                            return (
                                <div key={order.guid}>
                                    <div>
                                        {order.order.Vendor?.name} - {order.name}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {optimizedRoute ? (
                <div className={"mt-4"}>
                    <OwButton title={"download_csv"} fn={downloadCsv} />
                </div>
            ) : (
                <div className={"mt-4"}>
                    <OwButton title={"start"} active={processingRouteActive} disableOnActive={true} fn={getRouteOptimization} />
                </div>
            )}
        </div>
    );
};
